import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Card from '../../components/common/Card';
import { getMyFees, getPaymentSettings, submitProof } from '../../services/feeService';
import { isMethodEnabled } from '../../utils/paymentMethods';
import {
  MdPayments, MdCheckCircle, MdClose,
  MdHourglassTop, MdDoNotDisturb, MdInfo,
  MdUpload, MdWarning, MdOpenInNew, MdQrCode2,
  MdAccountBalance,
} from 'react-icons/md';
import { useCalendar } from '../../context/CalendarContext';
import { adHint } from '../../utils/calendar';
import DualDate from '../../components/common/DualDate';

// Asia/Kathmandu (UTC+5:45, no DST) — shift the epoch and read UTC parts so the month never
// flips a day early/late at the boundary. No UTC date math.
const KTH_OFFSET_MS = (5 * 60 + 45) * 60 * 1000;
const kthNow     = () => new Date(Date.now() + KTH_OFFSET_MS);
const THIS_MONTH = `${kthNow().getUTCFullYear()}-${String(kthNow().getUTCMonth() + 1).padStart(2, '0')}`;
const addMonths  = (ym, n) => {
  const [y, m] = String(ym).split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
// adHint (muted AD hint beside a BS month label) now lives in utils/calendar.

const fmtNPR = (n) => `NPR ${Number(n || 0).toLocaleString()}`;

const STATUS = {
  pending:         { label: 'Fee Due',                bg: 'bg-amber-500/15', border: 'border-amber-500/20', text: 'text-amber-400', icon: MdHourglassTop,  iconColor: 'text-amber-500' },
  overdue:         { label: 'Overdue',                bg: 'bg-red-50',    border: 'border-red-300',    text: 'text-red-700',    icon: MdHourglassTop,  iconColor: 'text-red-500'    },
  completed:       { label: 'Paid',                   bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-700',  icon: MdCheckCircle,   iconColor: 'text-green-500'  },
  waived:          { label: 'Waived',                 bg: 'bg-gray-50',   border: 'border-gray-200',   text: 'text-gray-600',   icon: MdDoNotDisturb,  iconColor: 'text-gray-400'   },
  failed:          { label: 'Failed',                 bg: 'bg-red-50',    border: 'border-red-200',    text: 'text-red-700',    icon: MdPayments,      iconColor: 'text-red-500'    },
  proof_submitted: { label: 'Waiting for approval',   bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   icon: MdHourglassTop,  iconColor: 'text-blue-500'   },
  // Partial is a normal, calm state — some paid, some still owed. Matches this page's fixed
  // pastel palette (the student portal predates the semantic-token system).
  partial:         { label: 'Partially Paid',         bg: 'bg-gray-50',   border: 'border-gray-200',   text: 'text-gray-700',   icon: MdHourglassTop,  iconColor: 'text-gray-500'   },
};

const HISTORY_BADGE = {
  pending:         'bg-amber-500/15 text-amber-400',
  overdue:         'bg-red-100 text-red-700',
  completed:       'bg-green-100 text-green-700',
  waived:          'bg-gray-100 text-gray-500',
  failed:          'bg-red-100 text-red-600',
  proof_submitted: 'bg-blue-100 text-blue-700',
  partial:         'bg-gray-100 text-gray-700',
};

const METHOD_OPTS = [
  { value: 'esewa',   label: 'eSewa'         },
  { value: 'khalti',  label: 'Khalti'        },
  { value: 'fonepay', label: 'Fonepay'       },
  { value: 'bank',    label: 'Bank Transfer' },
  { value: 'cash',    label: 'Cash'          },
  { value: 'other',   label: 'Other'         },
];

const PROOF_ALLOWED = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf'];
const MAX_SIZE      = 5 * 1024 * 1024;

// ── Proof Upload Modal ────────────────────────────────────────────────────────

// `months` are chosen on the page (arrears + advance) and carried into the submission, so the
// admin's approval marks exactly those months. The month picker lives on the page, not here.
function ProofModal({ months = [], total = 0, defaultMethod, accepted = [], onClose, onSuccess }) {
  const { formatMonth } = useCalendar();
  const fileRef = useRef();
  // Only methods the club enabled; keep the passed-in default if it's still allowed.
  const methodOpts   = METHOD_OPTS.filter(m => isMethodEnabled(m.value, accepted));
  const initialMethod = (defaultMethod && isMethodEnabled(defaultMethod, accepted))
    ? defaultMethod : (methodOpts[0]?.value || 'other');
  const [method,    setMethod]    = useState(initialMethod);
  const [amount,    setAmount]    = useState(String(total || ''));
  const [paidDate,  setPaidDate]  = useState(kthNow().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [file,      setFile]      = useState(null);

  // Partial support: only for a SINGLE month (multi-month is full-per-month, split server-side).
  // `total` is the outstanding balance of the selection.
  const singleMonth    = months.length === 1;
  const parsed         = Number(String(amount).replace(/,/g, ''));
  // Paying more than the outstanding balance isn't blocked — likely this month in full plus
  // next month in advance. Nothing is settled here; it's just a claim pending admin review,
  // who'll see the forward-settlement preview when approving it.
  const amountOver     = singleMonth && Number.isFinite(parsed) && parsed > total;
  const amountInvalid  = singleMonth && !(Number.isFinite(parsed) && parsed > 0);
  const isPartial      = singleMonth && Number.isFinite(parsed) && parsed > 0 && parsed < total;
  const remainingAfter = singleMonth ? Math.max(0, total - parsed) : 0;

  const mutation = useMutation({
    mutationFn: () => {
      const fd = new FormData();
      fd.append('months',    JSON.stringify(months)); // multi-month: arrears + advance
      fd.append('method',    method);
      if (amount)    fd.append('amount',    amount);
      if (paidDate)  fd.append('paidDate',  paidDate);
      if (reference) fd.append('reference', reference);
      if (file)      fd.append('proofFile', file);
      return submitProof(fd);
    },
    onSuccess: (r) => { toast.success(r.data.message); onSuccess(); },
    onError:   (e) => toast.error(e.response?.data?.message || 'Upload failed'),
  });

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!PROOF_ALLOWED.includes(f.type)) {
      toast.error('Only JPEG, PNG, WEBP, or PDF allowed');
      return;
    }
    if (f.size > MAX_SIZE) {
      toast.error('File must be under 5 MB');
      return;
    }
    setFile(f);
  };

  const handleSubmit = () => {
    if (!file) {
      toast.error('Please upload payment proof before submitting.');
      return;
    }
    mutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-card rounded-2xl shadow-2xl w-full max-w-sm max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-divider sticky top-0 bg-card rounded-t-2xl z-10">
          <h2 className="font-bold text-secondary text-base">Upload Payment Screenshot</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-muted hover:bg-nav-hover">
            <MdClose className="text-xl" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {/* Read-only recap of what's being paid — chosen on the page and carried into approval. */}
          <div className="rounded-xl bg-nav-hover px-4 py-3">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted mb-1">
              Paying for {months.length} month{months.length !== 1 ? 's' : ''}
            </p>
            <p className="text-sm text-secondary leading-snug">
              {months.map(mo => `${formatMonth(mo)} · ${adHint(mo)}`).join(', ')}
            </p>
            <p className="text-base font-bold text-secondary mt-1.5">{fmtNPR(total)}</p>
          </div>

          <div>
            <p className="text-xs font-medium text-muted mb-1.5">Payment Method</p>
            <div className="grid grid-cols-3 gap-2">
              {methodOpts.map(m => (
                <button
                  key={m.value}
                  type="button"
                  onClick={() => setMethod(m.value)}
                  className={`py-2 rounded-xl text-xs font-medium border-2 transition-all ${
                    method === m.value
                      ? 'border-primary bg-red-500/15 text-primary'
                      : 'border-divider text-muted hover:border-secondary'
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <p className="text-xs font-medium text-muted mb-1.5">Amount Paid (NPR)</p>
              <input
                type="number" min="0"
                value={amount}
                onChange={e => setAmount(e.target.value)}
                className="w-full border border-divider rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="e.g. 1500"
              />
            </div>
            <div>
              <p className="text-xs font-medium text-muted mb-1.5">Date Paid</p>
              <input
                type="date"
                value={paidDate}
                onChange={e => setPaidDate(e.target.value)}
                className="w-full border border-divider rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              />
            </div>
          </div>

          {/* Outstanding helper + calm partial preview (single month) / total (multi-month). */}
          {singleMonth ? (
            <div className="-mt-1 space-y-1.5">
              <p className="text-xs text-muted">Outstanding: <span className="font-semibold text-secondary">NPR {Number(total).toLocaleString()}</span></p>
              {amountOver ? (
                <div className="rounded-xl border border-divider bg-nav-hover px-3 py-2.5">
                  <p className="text-sm font-medium text-secondary">This is more than what's due.</p>
                  <p className="text-xs text-muted mt-0.5">If approved, the extra NPR {(parsed - total).toLocaleString()} will be applied to next month.</p>
                </div>
              ) : isPartial ? (
                <div className="rounded-xl border border-divider bg-nav-hover px-3 py-2.5">
                  <p className="text-sm font-medium text-secondary">Paying NPR {parsed.toLocaleString()}</p>
                  <p className="text-xs text-muted mt-0.5">NPR {remainingAfter.toLocaleString()} will remain outstanding</p>
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-xs text-muted -mt-1">Covers {months.length} months · NPR {Number(total).toLocaleString()} total</p>
          )}

          <div>
            <p className="text-xs font-medium text-muted mb-1.5">Transaction Reference (optional)</p>
            <input
              value={reference}
              onChange={e => setReference(e.target.value)}
              placeholder="e.g. TXN-12345"
              className="w-full border border-divider rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
          </div>

          {/* File upload */}
          <div>
            <p className="text-xs font-medium text-muted mb-1.5">Payment Screenshot</p>
            <input ref={fileRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={handleFile} />
            {file ? (
              <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl">
                <MdCheckCircle className="text-green-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium text-green-700 truncate">{file.name}</p>
                  <p className="text-[10px] text-green-500">{(file.size / 1024).toFixed(0)} KB</p>
                </div>
                <button onClick={() => setFile(null)} className="text-muted hover:text-muted"><MdClose /></button>
              </div>
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                className="w-full flex flex-col items-center justify-center py-5 border-2 border-dashed border-divider rounded-xl text-muted hover:border-red-500/40 hover:text-primary transition-colors"
              >
                <MdUpload className="text-2xl mb-1" />
                <span className="text-xs">Tap to upload screenshot</span>
                <span className="text-[10px] text-subtle mt-0.5">JPG, PNG, WEBP, PDF — max 5 MB</span>
              </button>
            )}
          </div>

          {!file && (
            <p className="text-xs text-amber-600">Please upload payment proof before submitting.</p>
          )}

          <p className="text-xs text-muted bg-nav-hover rounded-xl px-4 py-3">
            <MdInfo className="inline mr-1 text-muted" />
            Admin will review your screenshot and mark the fee as paid.
            You will receive a notification once approved.
          </p>

          <div className="flex gap-2 pt-1">
            <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-divider text-sm text-muted hover:bg-nav-hover transition-colors">
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={mutation.isPending || !file || amountInvalid}
              className="flex-1 py-2.5 rounded-xl text-white text-sm font-semibold bg-primary hover:bg-primary-dark transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {mutation.isPending ? 'Submitting…' : (isPartial ? `Submit NPR ${parsed.toLocaleString()}` : 'Submit Proof')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── QR Payment Card ───────────────────────────────────────────────────────────

function QrCard({ method, label, id, qrUrl, color, onPay }) {
  const [imgOpen, setImgOpen] = useState(false);
  return (
    <div className={`rounded-2xl border-2 p-4 ${color.border} ${color.bg} flex flex-col gap-2`}>
      <div className="flex items-center justify-between">
        <p className={`text-sm font-bold ${color.text}`}>{label}</p>
        <MdQrCode2 className={`text-lg ${color.text} opacity-60`} />
      </div>
      {id && <p className="text-xs font-mono text-muted">{id}</p>}

      {qrUrl ? (
        <button onClick={() => setImgOpen(true)} className="relative group">
          <img
            src={qrUrl}
            alt={`${label} QR code`}
            className="w-full aspect-square object-contain bg-card rounded-xl border border-divider hover:opacity-90 transition-opacity"
          />
          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <span className="bg-black/50 text-white text-xs px-2 py-1 rounded-lg flex items-center gap-1">
              <MdOpenInNew className="text-sm" /> Enlarge
            </span>
          </div>
        </button>
      ) : (
        <div className="w-full aspect-square bg-card rounded-xl border border-dashed border-divider flex items-center justify-center">
          <p className="text-xs text-subtle text-center px-2">QR not set yet.<br />Ask admin.</p>
        </div>
      )}

      <p className="text-[10px] text-muted text-center">After paying, upload screenshot below</p>

      <button
        onClick={() => onPay(method)}
        className={`w-full py-2 rounded-xl text-xs font-semibold border-2 ${color.border} ${color.text} hover:opacity-80 transition-opacity`}
      >
        I paid with {label}
      </button>

      {imgOpen && qrUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={() => setImgOpen(false)}
        >
          <div className="bg-card rounded-2xl p-4 max-w-sm w-full">
            <div className="flex items-center justify-between mb-3">
              <p className="font-semibold text-secondary">{label} QR</p>
              <button onClick={() => setImgOpen(false)} className="text-muted hover:text-muted"><MdClose /></button>
            </div>
            <img src={qrUrl} alt={`${label} QR`} className="w-full rounded-xl border border-divider" />
            {id && <p className="text-center text-xs font-mono text-muted mt-2">{id}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function FeePayment() {
  document.title = 'Fee Payment — TrainAxis';
  const { formatDate, formatMonth } = useCalendar();
  const qc = useQueryClient();
  const [modal,    setModal]    = useState(null); // null | method string
  // null = untouched → every owed month is pre-selected. Once the student toggles a row it
  // becomes an explicit array, so no effect is needed to seed it.
  const [selected, setSelected] = useState(null);

  const { data, isPending } = useQuery({
    queryKey: ['my-fees'],
    queryFn:  () => getMyFees().then(r => r.data),
  });

  const { data: psData } = useQuery({
    queryKey: ['payment-settings-student'],
    queryFn:  () => getPaymentSettings().then(r => r.data),
  });

  const fees       = data?.fees        || [];
  const monthlyFee = data?.monthlyFee  || 1500; // this student's effective fee (override-aware)
  const dueDay     = data?.dueDay      || 1;
  const ps         = psData?.paymentSettings || {};
  const accepted   = ps.acceptedPaymentMethods || []; // methods the club enabled (empty → all)

  const refresh = () => {
    setModal(null);
    setSelected(null); // submitted months move to proof_submitted → re-derive the default selection
    qc.invalidateQueries({ queryKey: ['my-fees'] });
  };

  if (isPending) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map(i => <div key={i} className="h-28 bg-nav-hover animate-pulse rounded-2xl" />)}
      </div>
    );
  }

  const dueDate = `${dueDay}${dueDay === 1 ? 'st' : dueDay === 2 ? 'nd' : dueDay === 3 ? 'rd' : 'th'} of each month`;

  // ── Month model ──────────────────────────────────────────────────────────────
  // Monthly fee docs only (custom charges are billed separately and aren't payable here).
  const monthly = fees.filter(f => (f.type || 'monthly') !== 'custom' && f.month);
  const byMonth = new Map(monthly.map(f => [f.month, f]));

  // OWED = anything unpaid, oldest first. Not just "this month" — the old page gated the whole
  // pay section on the current month's status, which hid arrears once this month was paid.
  const owedFees  = monthly
    .filter(f => f.status === 'pending' || f.status === 'overdue')
    .sort((a, b) => (a.month < b.month ? -1 : 1));
  const owedTotal = owedFees.reduce((s, f) => s + ((f.totalAmount != null ? f.totalAmount : f.amount) || 0), 0);

  // Awaiting review / rejected — surfaced as notices, and those months aren't selectable.
  const awaiting  = monthly.filter(f => f.status === 'proof_submitted').sort((a, b) => (a.month < b.month ? -1 : 1));
  const rejected  = monthly.filter(f => f.status === 'pending' && f.proofRejectionReason);

  // Selectable rows: unpaid months (oldest first) + the advance window (current + next 3), with
  // paid/waived/awaiting months excluded so a month can never be paid twice.
  const advanceWindow = (data?.upcomingMonths?.length ? data.upcomingMonths : [0, 1, 2, 3].map(n => addMonths(THIS_MONTH, n)));
  const blockedStatus = ['completed', 'waived', 'proof_submitted'];
  const owedRows = owedFees.map(f => ({
    month:   f.month,
    amount:  (f.totalAmount != null ? f.totalAmount : f.amount) || 0,
    base:    f.amount || 0,
    lateFee: f.lateFee || 0,
    discount: f.discount || 0,
    advance: false,
  }));
  const advanceRows = advanceWindow
    .filter(mo => !byMonth.has(mo) || !blockedStatus.includes(byMonth.get(mo).status))
    .filter(mo => !owedRows.some(r => r.month === mo))   // already listed as owed
    .map(mo => {
      const f = byMonth.get(mo);
      return {
        month:   mo,
        amount:  f ? ((f.totalAmount != null ? f.totalAmount : f.amount) || 0) : monthlyFee,
        base:    f ? (f.amount || 0) : monthlyFee,
        lateFee: f?.lateFee || 0,
        discount: f?.discount || 0,
        advance: true,   // paying ahead — no fee doc yet, or a future month
      };
    });
  const payRows = [...owedRows, ...advanceRows];

  // Default selection = everything owed (oldest first). Explicit once the student toggles.
  const sel          = selected ?? owedRows.map(r => r.month);
  const selectedRows = payRows.filter(r => sel.includes(r.month));
  const payTotal     = selectedRows.reduce((s, r) => s + r.amount, 0);
  const allClear     = owedFees.length === 0 && awaiting.length === 0;
  const toggleMonth  = (mo) => setSelected(prev => {
    const cur = prev ?? owedRows.map(r => r.month);
    return cur.includes(mo) ? cur.filter(m => m !== mo) : [...cur, mo];
  });

  const QR_CARDS = [
    ps.esewaId   || ps.esewaQr   ? { method: 'esewa',   label: 'eSewa',   id: ps.esewaId,   qrUrl: ps.esewaQr?.url,   color: { bg: 'bg-green-50',   border: 'border-green-200',   text: 'text-green-700'   } } : null,
    ps.khaltiId  || ps.khaltiQr  ? { method: 'khalti',  label: 'Khalti',  id: ps.khaltiId,  qrUrl: ps.khaltiQr?.url,  color: { bg: 'bg-purple-50',  border: 'border-purple-200',  text: 'text-purple-700'  } } : null,
    ps.fonepayId || ps.fonepayQr ? { method: 'fonepay', label: 'Fonepay', id: ps.fonepayId, qrUrl: ps.fonepayQr?.url, color: { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-700'    } } : null,
    ps.bankAccountNumber || ps.bankQr ? { method: 'bank', label: 'Bank Transfer', id: ps.bankAccountNumber ? `${ps.bankName ? ps.bankName + ' · ' : ''}${ps.bankAccountNumber}` : null, qrUrl: ps.bankQr?.url, color: { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-700' } } : null,
  ].filter(c => c && isMethodEnabled(c.method, accepted));  // only methods the club enabled

  return (
    <div className="space-y-5 max-w-xl mx-auto">
      <div>
        <h1 className="text-2xl font-bold text-secondary">Fee Payment</h1>
        <p className="text-sm text-muted mt-0.5">Monthly fee — due on the {dueDate}</p>
      </div>

      {/* ── 1 · STATUS ── one glanceable card: what you owe, or a calm all-clear ── */}
      {allClear ? (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-5 flex items-start gap-4">
          <MdCheckCircle className="text-emerald-500 text-3xl flex-shrink-0" />
          <div>
            <p className="text-base font-semibold text-secondary">All paid up — nothing due</p>
            <p className="text-xs text-muted mt-0.5">
              You're up to date on your monthly fees. You can still pay ahead for upcoming months below.
            </p>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-divider bg-card p-5">
          <p className="text-xs font-semibold uppercase tracking-wide text-muted">You owe</p>
          <p className="text-3xl font-bold text-secondary mt-0.5">{fmtNPR(owedTotal)}</p>
          <div className="mt-3 space-y-2">
            {owedFees.map(f => (
              <div key={f._id} className="space-y-0.5">
                <div className="flex items-center justify-between gap-3">
                  <span className="text-sm text-secondary truncate">
                    {formatMonth(f.month)}<span className="text-muted"> · {adHint(f.month)}</span>
                    {f.status === 'overdue' && (
                      <span className="ml-2 text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-red-500/15 text-red-600">overdue</span>
                    )}
                  </span>
                  <span className="text-sm font-medium text-secondary whitespace-nowrap">{fmtNPR(f.amount)}</span>
                </div>
                {f.lateFee > 0 && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-red-500">Late fee</span>
                    <span className="text-xs text-red-500 whitespace-nowrap">+ {fmtNPR(f.lateFee)}</span>
                  </div>
                )}
                {f.discount > 0 && (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-emerald-600">Discount</span>
                    <span className="text-xs text-emerald-600 whitespace-nowrap">− {fmtNPR(f.discount)}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Awaiting review (per month, not just the current one) ── */}
      {awaiting.length > 0 && (
        <div className="rounded-2xl border border-blue-500/30 bg-blue-500/10 p-4 flex items-start gap-3">
          <MdHourglassTop className="text-blue-500 text-2xl flex-shrink-0" />
          <div>
            <p className="font-semibold text-secondary text-sm">Waiting for admin confirmation</p>
            <p className="text-xs text-muted mt-0.5">
              {awaiting.map(f => `${formatMonth(f.month)} · ${adHint(f.month)}`).join(', ')} — submitted, pending review.
              You'll get a notification once approved.
            </p>
          </div>
        </div>
      )}

      {/* ── Rejection notice ── */}
      {rejected.length > 0 && (
        <div className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4 flex items-start gap-3">
          <MdWarning className="text-red-500 text-2xl flex-shrink-0" />
          <div>
            <p className="font-semibold text-secondary text-sm">Payment proof not accepted</p>
            <p className="text-xs text-muted mt-0.5">{rejected[0].proofRejectionReason}</p>
            <p className="text-xs text-muted mt-1">Select the month below and upload a new screenshot.</p>
          </div>
        </div>
      )}

      {/* ── 2 · PAY ── always visible, whatever the balance ── */}
      <div className="rounded-2xl border border-divider bg-card p-5">
        <p className="text-sm font-semibold text-secondary">Pay your fee</p>
        <p className="text-xs text-muted mt-0.5 mb-3">Select the months you're paying for — you can pay ahead too.</p>

        {payRows.length === 0 ? (
          <p className="text-sm text-muted py-4 text-center">Nothing to pay right now.</p>
        ) : (
          <>
            <div className="space-y-1.5">
              {payRows.map(r => {
                const on = sel.includes(r.month);
                return (
                  <button
                    key={r.month}
                    type="button"
                    onClick={() => toggleMonth(r.month)}
                    className={`w-full min-h-[44px] flex items-center justify-between gap-3 px-3 py-2 rounded-xl border text-left transition-colors duration-150 ${
                      on ? 'border-secondary bg-nav-hover' : 'border-divider hover:border-secondary'
                    }`}
                  >
                    <span className="flex items-center gap-2.5 min-w-0">
                      <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${on ? 'border-secondary bg-secondary' : 'border-divider'}`}>
                        {on && <MdCheckCircle className="text-card text-[10px]" />}
                      </span>
                      <span className="text-sm text-secondary truncate">
                        {formatMonth(r.month)}<span className="text-muted"> · {adHint(r.month)}</span>
                      </span>
                      {r.advance && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-nav-hover text-muted border border-divider flex-shrink-0">
                          advance
                        </span>
                      )}
                    </span>
                    <span className="text-sm font-medium text-secondary whitespace-nowrap flex-shrink-0">{fmtNPR(r.amount)}</span>
                  </button>
                );
              })}
            </div>

            {/* Running total */}
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-divider">
              <span className="text-sm text-muted">
                {selectedRows.length === 0
                  ? 'Select a month to continue'
                  : `Paying ${selectedRows.length} month${selectedRows.length !== 1 ? 's' : ''}`}
              </span>
              <span className="text-lg font-bold text-secondary">{fmtNPR(payTotal)}</span>
            </div>

            {ps.paymentInstructions && (
              <div className="mt-3 rounded-xl bg-nav-hover px-4 py-3 flex items-start gap-2">
                <MdInfo className="text-muted flex-shrink-0 mt-0.5" />
                <p className="text-xs text-muted">{ps.paymentInstructions}</p>
              </div>
            )}

            {/* Methods — only what the club accepts */}
            {selectedRows.length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted mb-2">Pay with</p>
                {QR_CARDS.length > 0 && (
                  <div className="grid grid-cols-2 gap-3">
                    {QR_CARDS.map(card => (
                      <QrCard key={card.method} {...card} onPay={(m) => setModal(m)} />
                    ))}
                  </div>
                )}

                {/* Cash — nothing to scan; the admin records it at the club. */}
                {isMethodEnabled('cash', accepted) && (
                  <div className="mt-3 rounded-xl border border-divider p-3 flex items-start gap-2.5">
                    <MdPayments className="text-muted text-lg flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-secondary">Cash</p>
                      <p className="text-xs text-muted mt-0.5">Pay at the club — the admin will record it.</p>
                    </div>
                  </div>
                )}

                <button
                  onClick={() => setModal('cash')}
                  className="mt-3 w-full min-h-[44px] py-3 rounded-2xl border-2 border-dashed border-divider text-sm text-muted transition-colors duration-150 hover:border-secondary hover:text-secondary flex items-center justify-center gap-2"
                >
                  <MdUpload className="text-lg" />
                  Already paid? Upload your screenshot
                </button>
              </div>
            )}

            {QR_CARDS.length === 0 && selectedRows.length > 0 && !isMethodEnabled('cash', accepted) && (
              <div className="mt-3 rounded-xl border border-divider p-4 text-center">
                <MdAccountBalance size={32} className="mx-auto text-subtle mb-2" />
                <p className="text-sm text-secondary font-medium">Payment details coming soon</p>
                <p className="text-xs text-muted mt-1">This club hasn't added payment methods yet. Please contact your club to arrange payment.</p>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Payment History ── */}
      <Card>
        <h3 className="font-semibold text-secondary text-sm mb-4">Payment History</h3>
        {!fees.length ? (
          <div className="py-8 text-center">
            <MdPayments className="text-4xl text-subtle mx-auto mb-2" />
            <p className="text-muted text-sm">No payment records yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {fees.map(fee => {
              const conf = STATUS[fee.status] || STATUS.pending;
              const Icon = conf.icon;
              return (
                <div key={fee._id} className={`flex items-center gap-3 p-3 rounded-xl border ${conf.bg} ${conf.border}`}>
                  <Icon className={`flex-shrink-0 text-xl ${conf.iconColor}`} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-secondary">
                      {formatMonth(fee.month)}
                      {fee.isProrated && fee.billableDays ? ` (${fee.billableDays} days)` : ''}
                    </p>
                    <p className="text-xs text-muted">
                      {METHOD_OPTS.find(m => m.value === fee.method)?.label || fee.method}
                      {(fee.paidDate || fee.paidAt) && <> · <DualDate date={fee.paidDate || fee.paidAt} /></>}
                    </p>
                    {fee.receiptNumber && <p className="text-[10px] text-muted font-mono mt-0.5">#{fee.receiptNumber}</p>}
                    {fee.status === 'proof_submitted' && <p className="text-[10px] text-blue-500 mt-0.5">Screenshot submitted — waiting for approval</p>}
                    {fee.status === 'pending' && fee.proofRejectionReason && (
                      <p className="text-[10px] text-red-500 mt-0.5">Proof rejected — please resubmit</p>
                    )}
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-bold text-secondary">{fmtNPR(fee.totalAmount || fee.amount)}</p>
                    {fee.isProrated && (
                      <span className="inline-block text-[9px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded-md bg-amber-50 text-amber-700 border border-amber-200 mt-0.5">Partial</span>
                    )}
                    {fee.lateFee > 0 && <p className="text-[10px] text-red-500">+{fee.lateFee.toLocaleString()} late</p>}
                    {/* Partial — show what's paid and what's still owed (Stage 2 fields). */}
                    {fee.status === 'partial' && (
                      <p className="text-[10px] text-gray-600">Paid {fmtNPR(fee.amountPaid)} · {fmtNPR(fee.balance)} left</p>
                    )}
                    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${HISTORY_BADGE[fee.status]}`}>
                      {conf.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* ── Proof modal ── */}
      {modal !== null && (
        <ProofModal
          months={selectedRows.map(r => r.month)}
          total={payTotal}
          defaultMethod={modal}
          accepted={accepted}
          onClose={() => setModal(null)}
          onSuccess={refresh}
        />
      )}
    </div>
  );
}
